<template>
  <div class="f-file_detail absolute-cover z-1 bg-black overflow-hidden">

    <section
      class="f-file_detail-main_container transition-{right} duration-500 absolute inset-0"
      :class="isPanelVisible ? 'sm:right-[350px]' : 'panel-hidden'"
      >
      <div class="z-20 relative flex items-center bg-gradient-to-b from-black/50 to-90% p-1 dark">

        <u-tooltip :text="backButtonText">
          <core-selection-toolbar-button
            dark
            size="lg"
            icon="i-ri-arrow-left-long-line"
            @click="exitDetails"
          />
        </u-tooltip>

        <file-detail-toolbar-digitization
          v-if="isDigitization"
          :is-panel-visible="isPanelVisible"
          :is-download-pending="isDownloadPending"
          @toggle-panel="togglePanel"
        />
        <file-detail-toolbar
          v-else-if="file.is.detail"
          class="m-0 p-0"
          :is-panel-visible="isPanelVisible"
          :is-download-pending="isDownloadPending"
          @toggle-panel="togglePanel"
          @download-file="downloadFile"
          @removed="onFileRemovedFromCollection"
        />

      </div>

      <file-detail-stage
        ref="detailStageComp"
        :available-space="stageRectangle"
        :is-loading="isLoading"
        @exit="exitDetails"
        @download="downloadFile"
      />

    </section>

    <section
      :class="[
        'absolute top-0 bottom-0 right-0 w-full xs:w-[350px] bg-charcoal-800 dark pt-3 px-3.5 z-20 scrollbar-light',
        {
          'translate-x-full': !isPanelVisible,
          'duration-500 transition-transform': isInfoPanelReady,
          'overflow-y-auto': !isDatePickerOpen,
        }
      ]"
     >
      <div class="flex gap-2 items-center">
        <span class="text-2xl flex-1 text-white">Info</span>
        <u-button
          color="white"
          variant="ghost"
          :icon="COMMON_ICONS.close"
          @click="togglePanel(true)"
          :ui="{color: {white: {ghost: 'text-white hover:bg-white/15'}}}"
        />
      </div>


      <file-detail-info-digitization v-if="isDigitization" />
      <file-detail-info
        v-else
        @file-remove-from-collection="onFileRemovedFromCollection"
        @update-date-picker-opened="isOpen => (isDatePickerOpen = isOpen)"
      />
    </section>
  </div>
</template>

<script setup>
  import {useStorage} from '@vueuse/core'
  import {storeToRefs} from 'pinia';
  import debounce from 'lodash.debounce';

  const INFO_PANEL_WIDTH = 350;
  const INFO_PANEL_SHOULD_SHOW_STORAGE_KEY = 'f-file-detail-info-panel-should-show';

  const fileContext = inject('fileContext');

  const filesStore = useFilesStore();
  const {file, files} = storeToRefs(filesStore);

  const fileActions = useFileActions();
  const buildRoute = useBuildRoute();
  const router = useRouter();
  const route = useRoute();
  const exitToRoute = inject('exitToRoute', null);

  const detailStageComp = ref();
  const isLoading = ref(false);

  const isPanelVisibleStored = useStorage(INFO_PANEL_SHOULD_SHOW_STORAGE_KEY, true, sessionStorage);
  const isPanelVisible = ref(isPanelVisibleStored.value !== null ? isPanelVisibleStored.value : true);
  const isInfoPanelReady = ref(false);
  const stageRectangle = ref(null);
  const isDownloadPending = ref(false);

  const isDigitization = fileContext.contextType === FILE_CONTEXTS.digitization;
  const backButtonText = computed(() => {
    const fileUserName = file.value.user?.name;

    if (exitToRoute) {
      return 'Back';
    }

    if (fileContext.contextType === FILE_CONTEXTS.file && fileUserName) {
      return `${fileUserName}'s Profile`;
    }
    return 'Back';
  });

  const {width: stageWidth, height: stageHeight} = useElementSize(detailStageComp);
  const {width: windowWidth, height: windowHeight} = useWindowSize();
  const isMobileScreenWidth = computed(() => windowWidth.value < 768);

  const isDatePickerOpen = ref(false);

  function togglePanel(shouldPersist = true) {
    isPanelVisible.value = !isPanelVisible.value;

    if (!isMobileScreenWidth.value) {
      const widthAdjustment = INFO_PANEL_WIDTH * (isPanelVisible.value ? -1 : 1);
      updateStageDimensions({widthAdjustment});
    }

    if (shouldPersist) {
      isPanelVisibleStored.value = isPanelVisible.value;
    }
  }

  function exitDetails() {
    if (exitToRoute) {
      return navigateTo(exitToRoute);
    }

    const id = fileContext.contextId;
    const slug = fileContext.contextSlug;
    const userId = fileContext.userId;
    const shareToken = fileContext.shareToken;
    const query = fileContext.query;

    switch (fileContext.contextType) {
      case FILE_CONTEXTS.file:
        return navigateTo(buildRoute.toUserProfile({user: file.value.user}));
      case FILE_CONTEXTS.album:
        return navigateTo(buildRoute.toAlbum({
          album: {slug},
          userId,
          shareToken,
          query
        }));
      case FILE_CONTEXTS.tag:
        return navigateTo(buildRoute.toTag({
          tag: {slug},
          userId,
          shareToken,
          query
        }));
      case FILE_CONTEXTS.person:
        return navigateTo(buildRoute.toPerson({
          person: {slug},
          userId,
          shareToken,
          query
        }));
      case FILE_CONTEXTS.container:
        return navigateTo(buildRoute.toContainer({
          container: {id},
          userId,
          shareToken,
        }));
      case FILE_CONTEXTS.project:
        return navigateTo(buildRoute.toProjects());
      case FILE_CONTEXTS.digitization:
        if (id) { //project
          return navigateTo(useBuildRoute().toDigitizationProject({boxId: id, shareToken}));
        }

        //single file
        return navigateTo(buildRoute.toUserProfile({user: file.value.user}));
      default:
        navigateTo('/all');
    }
  }

  function downloadFile() {
    if (!isDownloadPending.value) {
      isDownloadPending.value = true;
      fileActions.downloadFiles({files: file.value, userId: file.value.user.id});

      setTimeout(() => (isDownloadPending.value = false), 5000);
    }
  }

  function onFileRemovedFromCollection({fileIndexInStore}) {
    if (files.value.length === 0) {
      return exitDetails();
    }

    const toFile = files.value[Math.max(Math.min(fileIndexInStore, files.value.length - 1), 0)];

    router.push({
      params: {
        file_id: toFile.id
      },
      query: route.query,
      hash: route.hash
    });
  }

  const {modals} = useFModals();

  function escHandler(e) {
    if (!modals.value.length && e.keyCode === 27) {
      exitDetails();
    }
  }

  function updateStageDimensions({widthAdjustment = 0} = {}) {
    stageRectangle.value = Object.assign({
      height: stageHeight.value,
      width: stageWidth.value + widthAdjustment
    });
  }

  const handleResize = debounce(async () => {
    await nextTick();
    updateStageDimensions()
    //to fix a bug with slower mobile browsers
    await waitFor(750);
    updateStageDimensions();
  }, 100);

  watch(windowWidth, handleResize, {immediate: true});
  watch(windowHeight, handleResize, {immediate: true});

  onMounted(async () => {
    if (isMobileScreenWidth.value && isPanelVisible.value) { //bootstrap sm screen
      togglePanel(false);
    }

    //note: don't use the defineShortcuts composable for this - order of handler execution causes problems when modals are opened
    document.body.addEventListener('keydown', escHandler);

    await waitFor(300);

    isInfoPanelReady.value = true;
    updateStageDimensions();
  });

  onBeforeUnmount(() => {
    document.body.removeEventListener('keydown', escHandler);
  });

  watch(file, async () => {
    updateStageDimensions();
  });

</script>
